import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeliveryTracking = ({ channel = 'all' }) => {
  const [messages] = useState([
    {
      id: 1,
      patientName: 'Sarah Johnson',
      messageType: 'Appointment Reminder',
      channel: 'sms',
      sentTime: '2026-01-19T10:30:00',
      status: 'delivered',
      readTime: '2026-01-19T10:32:00',
      response: 'CONFIRM'
    },
    {
      id: 2,
      patientName: 'Michael Chen',
      messageType: 'Treatment Update',
      channel: 'email',
      sentTime: '2026-01-19T09:15:00',
      status: 'opened',
      readTime: '2026-01-19T09:45:00',
      response: null
    },
    {
      id: 3,
      patientName: 'Emily Rodriguez',
      messageType: 'Post-Procedure Instructions',
      channel: 'sms',
      sentTime: '2026-01-19T08:00:00',
      status: 'delivered',
      readTime: null,
      response: null
    },
    {
      id: 4,
      patientName: 'David Thompson',
      messageType: 'Medication Reminder',
      channel: 'email',
      sentTime: '2026-01-18T16:30:00',
      status: 'failed',
      readTime: null,
      response: null,
      errorMessage: 'Invalid email address'
    },
    {
      id: 5,
      patientName: 'Lisa Anderson',
      messageType: 'Follow-Up Care',
      channel: 'sms',
      sentTime: '2026-01-18T14:20:00',
      status: 'sent',
      readTime: null,
      response: null
    }
  ]);

  const filteredMessages = channel === 'all' 
    ? messages 
    : messages?.filter(m => m?.channel === channel);

  const stats = {
    total: filteredMessages?.length,
    delivered: filteredMessages?.filter(m => m?.status === 'delivered')?.length,
    opened: filteredMessages?.filter(m => m?.status === 'opened')?.length,
    failed: filteredMessages?.filter(m => m?.status === 'failed')?.length,
    responded: filteredMessages?.filter(m => m?.response)?.length
  };

  const deliveryRate = stats?.total > 0 ? ((stats?.delivered + stats?.opened) / stats?.total * 100)?.toFixed(1) : 0;
  const responseRate = stats?.total > 0 ? (stats?.responded / stats?.total * 100)?.toFixed(1) : 0;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return { name: 'CheckCircle', color: 'text-success' };
      case 'opened':
        return { name: 'Mail', color: 'text-primary' };
      case 'sent':
        return { name: 'Send', color: 'text-warning' };
      case 'failed':
        return { name: 'XCircle', color: 'text-error' };
      default:
        return { name: 'Clock', color: 'text-muted-foreground' };
    }
  };

  const getStatusLabel = (status) => {
    return status?.charAt(0)?.toUpperCase() + status?.slice(1);
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Activity" size={20} className="text-primary" />
          Delivery Tracking
        </h2>
        <Button variant="outline" size="sm" iconName="Filter" iconPosition="left">
          Advanced Filters
        </Button>
      </div>

      {/* Delivery Statistics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <div className="bg-muted/50 rounded-lg p-3 border border-border text-center">
          <p className="text-xs text-muted-foreground mb-1">Total Sent</p>
          <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
        </div>
        <div className="bg-success/10 rounded-lg p-3 border border-success/20 text-center">
          <p className="text-xs text-success mb-1">Delivered</p>
          <p className="text-2xl font-bold text-success">{stats?.delivered}</p>
        </div>
        <div className="bg-primary/10 rounded-lg p-3 border border-primary/20 text-center">
          <p className="text-xs text-primary mb-1">Opened</p>
          <p className="text-2xl font-bold text-primary">{stats?.opened}</p>
        </div>
        <div className="bg-error/10 rounded-lg p-3 border border-error/20 text-center">
          <p className="text-xs text-error mb-1">Failed</p>
          <p className="text-2xl font-bold text-error">{stats?.failed}</p>
        </div>
        <div className="bg-warning/10 rounded-lg p-3 border border-warning/20 text-center">
          <p className="text-xs text-warning mb-1">Responded</p>
          <p className="text-2xl font-bold text-warning">{stats?.responded}</p>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Delivery Rate</span>
            <span className="text-2xl font-bold text-success">{deliveryRate}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div className="bg-success h-2 rounded-full" style={{ width: `${deliveryRate}%` }}></div>
          </div>
        </div>
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Response Rate</span>
            <span className="text-2xl font-bold text-primary">{responseRate}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div className="bg-primary h-2 rounded-full" style={{ width: `${responseRate}%` }}></div>
          </div>
        </div>
      </div>

      {/* Message List */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Recent Messages</h3>
        {filteredMessages?.map((message) => {
          const statusIcon = getStatusIcon(message?.status);
          return (
            <div key={message?.id} className="bg-muted/30 rounded-lg p-4 border border-border hover:border-primary transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="font-semibold text-foreground">{message?.patientName}</p>
                  <p className="text-sm text-muted-foreground">{message?.messageType}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name={statusIcon?.name} size={20} className={statusIcon?.color} />
                  <span className={`text-xs font-medium ${statusIcon?.color}`}>
                    {getStatusLabel(message?.status)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div>
                  <p className="text-xs text-muted-foreground">Channel</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Icon name={message?.channel === 'sms' ? 'MessageSquare' : 'Mail'} size={14} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">{message?.channel?.toUpperCase()}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Sent Time</p>
                  <p className="text-sm font-medium text-foreground mt-1">
                    {new Date(message?.sentTime)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Read Time</p>
                  <p className="text-sm font-medium text-foreground mt-1">
                    {message?.readTime ? new Date(message?.readTime)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Response</p>
                  <p className="text-sm font-medium text-foreground mt-1">
                    {message?.response || '-'}
                  </p>
                </div>
              </div>

              {message?.errorMessage && (
                <div className="bg-error/10 border border-error rounded-lg p-2 mt-2">
                  <p className="text-xs text-error flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    {message?.errorMessage}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2 mt-3">
                <Button variant="ghost" size="xs" iconName="Eye" iconPosition="left">
                  View Details
                </Button>
                <Button variant="ghost" size="xs" iconName="RefreshCw" iconPosition="left">
                  Resend
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeliveryTracking;